import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { WindChart } from '../charts';
import {
  convertWindSpeedFromApi,
  enrichedSeriesToTimeSeries,
  formatWindSpeedHeader,
  getClosestEnrichedPoint,
  getForecastDayDomain,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { useGraphTime } from './GraphTimeContext';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from './GraphCardHeader';
import GraphCardNoData from './GraphCardNoData';

export interface WindCardProps {
  windPoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  isToday: boolean;
  latitude: number;
  longitude: number;
}

const WindCard: FC<WindCardProps> = ({
  windPoints,
  selectedDate,
  timeZone,
  isToday,
  latitude,
  longitude,
}) => {
  const unitsConfig = useSelector(selectUnitsConfig);

  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('wind');

  const windSeries = useMemo(
    () =>
      graphPointsToEnrichedSeries(
        windPoints,
        selectedDate,
        timeZone,
        convertWindSpeedFromApi,
      ),
    [selectedDate, timeZone, windPoints],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(windSeries, domain),
    [domain, windSeries],
  );

  const selectedWindPoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(windSeries, selectedChartTime);
  }, [selectedChartTime, windSeries]);

  const primaryDisplay = selectedWindPoint
    ? formatWindSpeedHeader(
        selectedWindPoint.value,
        unitsConfig,
        selectedWindPoint.directionText,
      )
    : '—';

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="Wind"
        testId="weather-forecast-wind-card"
        analyticsCard="wind"
      >
        <GraphCardNoData
          chartLabel="Wind"
          stationType="general"
          isLocationMissingData={windPoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Wind"
      testId="weather-forecast-wind-card"
      analyticsCard="wind"
    >
      <GraphCardHeader primary={primaryDisplay} />

      <Box width="100%">
        <WindChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          directionSeries={windSeries}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>
    </CollapsibleCard>
  );
};

export default WindCard;
